<template>
	<div class="common-layout">
		<el-container>
			<el-aside ref="leftHolder" :class="{'left-holder': true, 'collapsed': isMenuCollapsed, 'restored': !isMenuCollapsed, 'animation-ready': leftMenuAnimationReady}">
				<div style="height: 1.38rem;">
					<h1 v-if="!isTinyLogoShown" class="full-logo">TECHNICAL POINT</h1>
					<h1 v-if="isTinyLogoShown" class="tiny-logo">T</h1>
				</div>
				<application-left-menu-component 
					:width="isMenuCollapsed ? collapsedWidth : expandedWidth"
					:collapsed="isMenuCollapsed"
					@collapse="onLeftMenuCollapse"
					:is-authenticated="store.getters.isAuthenticated" />		
			</el-aside>			
			<el-container :class="{'content-holder': true, 'collapsed': isMenuCollapsed, 'restored': !isMenuCollapsed}">
				<router-view></router-view>
			</el-container>
		</el-container>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Document, Menu as IconMenu, Location, Setting, DataLine, Memo, Coin, User, Avatar } from '@element-plus/icons-vue';
import ApplicationLeftMenuComponent from '@/components/ApplicationLeftMenuComponent.vue';
import store from './store';
import router from './router';
import { ElNotification } from 'element-plus';
import UserSettingsModel from './models/UserSettingsModel';

@Options({ 
	components: {
		Document, IconMenu, Location, Setting, DataLine, Memo, Coin, User, Avatar,
		ApplicationLeftMenuComponent
	}
})
export default class App extends Vue {
	/** constants */
	store = store;
	collapsedWidth = 64;
	expandedWidth = 300;

	userSettings!: UserSettingsModel;

	/** view-models */
	leftHolder: HTMLElement | null = null;

	isTinyLogoShown = false;
	leftMenuAnimationReady = false;
	isMenuCollapsed = false;

	async mounted() {
		this.initLeftMenuElement();
		this.initUserSettings();

		if (this.isMenuCollapsed) {
			this.showTinyLogoDelayed();
			this.leftMenuAnimationReady = true;
		}

		/** load app dictionaries (allowed for anonymous users) */
		/** load user roles (auth-free) */
		store.dispatch('loadRolesAsync');
		/** load order statuses (auth-free) */
		store.dispatch('loadOrderStatusesAsync');

		/** authorize user */
		if (!store.getters.isAuthenticated) {
			router.push('/signin');
			return;
		}

		const tryAuthenticate = await this.tryAuthenticateWithRefreshToken();

		if (tryAuthenticate === true) {
			/** load entities are managed to interact with customers */
			store.dispatch('fetchExecutivesAsync');
			/** load items margin matrix (required for total price calculation while filling up an order with items) */
			store.dispatch('fetchPricesMarginMatrixAsync');
			router.push('/');
		} else if (tryAuthenticate === null) {
			ElNotification({
				message: 'Ошибка авторизации, повторите попытку позднее',
				type: 'error'
			});
		} else {
			ElNotification({
				message: 'Доступ запрещен',
				type: 'warning'
			});
			router.push('/signin');
		}
	}

	async tryAuthenticateWithRefreshToken() {
		try {
			const isExchanged = await store.dispatch('exchangeRefreshTokenForJwt');

			if (!isExchanged) {
				return false;
			}

			const isUserFetched = await store.dispatch('fetchUserAsync');
			if (!isUserFetched) {				
				return false;
			}

			return true;
		} catch {
			return null;
		}
	}

	initLeftMenuElement() {
		this.$nextTick().then(() => {
			this.leftHolder = (this.$refs.leftHolder as any)?.$el as HTMLElement;
			window.dispatchEvent(new Event('resize'));

			this.onLeftMenuCollapse(this.userSettings.leftMenuCollapse);
		});
	}

	initUserSettings() {
		this.userSettings = (store.getters.userSettings as UserSettingsModel)!;
	}

	onLeftMenuCollapse(menuCollapsed: boolean) {
		if (menuCollapsed) {
			this.leftMenuAnimationReady = true;
			this.showTinyLogoDelayed();
		} else {
			this.isTinyLogoShown = false;
		}
		
		this.userSettings!.leftMenuCollapse = menuCollapsed;
		store.commit('userSettings', this.userSettings);
		this.isMenuCollapsed = menuCollapsed;
	}

	showTinyLogoDelayed() {
		window.setTimeout(() => this.isTinyLogoShown = true, 333);
	}

}
</script>

<style>
body, html {
  height: 100%;
}

body {
	margin: 0;
	padding: 0;
}

a { 
	color: var(--el-menu-text-color);
	text-decoration: none;
}

h2 {
	text-align: left;
	font-weight: normal;
	font-size: 2.28rem;
}
	h2 span {
		color: var(--el-color-primary);
	}

h3 {
	text-align: left;
	font-weight: normal;
	font-size: 1.88rem;
}
	h3 span {
		color: var(--el-color-primary);
	}

ul.plain {
	margin: 0;
	padding: 0;
	list-style: none;
}

body {
	font-family: var(--el-font-family);
}

#app {
	font-family: var(--el-font-family);
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	text-align: center;
	color: #2c3e50;

	--collapsed-left-menu-width: 65px;
	--restored-left-menu-width: 300px;
}

#app .base-form {
	margin: 1rem;
	min-width: 350px;
	max-width: 650px;
	text-align: left;
}

	#app .base-form.header {
		margin-top: 0;
		margin-bottom: 1.2rem;
		max-width: unset;
	}

		#app .base-form.header h2 {
			margin-top: 0;
			margin-bottom: 1rem;
		}

		#app .base-form.header h3 {
			margin-top: 0;
			margin-bottom: .78rem;
		}
	
	#app .base-form.filter {
		margin-top: .4rem;
		margin-bottom: .6rem;
	}

	#app .base-form.form {
		margin-top: 0;
		margin-bottom: .6rem;
	}

#app .el-form-item--label-top {
	width: inherit;
}

	#app .el-form-item--label-top .el-form-item__label {
		margin-bottom: 2px;		
		font-size: .9rem;
	}

#app div.input-invalid {

}

	#app div.input-invalid .el-input__wrapper {
		box-shadow: 0 0 0 1px var(--el-color-danger) inset
	}

		#app div.input-invalid .el-input__wrapper input {
			color: var(--el-color-danger-dark-2)
		}

	#app aside {
		overflow: hidden;
		border-right: 1px solid var(--el-menu-border-color);
	}

	#app .left-holder {
		padding-top: .6rem;
		width: var(--restored-left-menu-width);
		position: fixed;
		height: 100%;
		transition: width ease-in-out .3s;
	}

	#app .left-holder .full-logo {
		font-size: 1.2rem;
		overflow: hidden;
		text-overflow: unset;		
		word-wrap: unset;
		/* height: 1.38rem; */
		margin: 0;
		opacity: 0;
	}

	#app .left-holder .tiny-logo {
		opacity: 0;
		margin: 0;
		font-size: 2rem;
		line-height: 1.38rem;		
	}

	#app .left-holder.restored {
		width: var(--restored-left-menu-width);
	}		

		#app .left-holder.restored:not(.animation-ready) .full-logo {
			display: block;
			opacity: 1;
		}

		#app .left-holder.restored.animation-ready .full-logo {
			animation: .3s ease-in-out .333s 1 fullLogoAppearAnimation forwards;
		}		

			@keyframes fullLogoAppearAnimation {
				0% {
					display: block;
					opacity: 0;
				}
				100% {
					display: block;
					opacity: 1;
				}
			}

		#app .left-holder.restored .tiny-logo {
			display: none;
		}		

	#app .left-holder.collapsed {
		width: var(--collapsed-left-menu-width);
	}

		#app .left-holder.collapsed .full-logo {
			animation: .15s ease-in-out 0s 1 fullLogoHideAnimation forwards;
		}

			@keyframes fullLogoHideAnimation {
				0% {
					width: var(--restored-left-menu-width);
					opacity: 1;
					/* height: 1.38rem; */
				}
				90% {
					width: var(--restored-left-menu-width);
					opacity: 0;
					/* height: 1.38rem; */
				}
				100% {
					width: 0;
					opacity: 0;
					/* height: 1.38rem; */
				}
			}

		#app .left-holder.collapsed .tiny-logo {
			animation: .6s ease-in-out 0s 1 tinyLogoAppearAnimation forwards;
		}

		@keyframes tinyLogoAppearAnimation {
			0% {
				color: var(--el-color-primary-light-9);
				transform: translateX(-1rem);
				opacity: 0;
			}
			100% {
				color: var(--el-color-primary);
				transform: translateX(0);
				opacity: 1;
			}
		}


	#app .content-holder {
		transition: margin-left ease-in-out .3s;
	}

		#app .content-holder.collapsed {		
			margin-left: var(--collapsed-left-menu-width);
		}

		#app .content-holder.restored {		
			margin-left: var(--restored-left-menu-width);
		}

	#app .el-tag.filled.el-tag--primary {
		--el-tag-bg-color: var(--el-color-primary-light-9);
		--el-tag-border-color: var(--el-color-primary-light-8);
		--el-tag-hover-color: var(--el-color-primary)
	}

	#app .el-tag.filled.el-tag--success {
		--el-tag-bg-color: var(--el-color-success-light-9);
		--el-tag-border-color: var(--el-color-success-light-8);
		--el-tag-hover-color: var(--el-color-success)
	}

	#app .el-tag.filled.el-tag--warning {
		--el-tag-bg-color: var(--el-color-warning-light-9);
		--el-tag-border-color: var(--el-color-warning-light-8);
		--el-tag-hover-color: var(--el-color-warning)
	}

	#app .el-tag.filled.el-tag--danger {
		--el-tag-bg-color: var(--el-color-danger-light-9);
		--el-tag-border-color: var(--el-color-danger-light-8);
		--el-tag-hover-color: var(--el-color-danger)
	}

	#app .el-tag.filled.el-tag--error {
		--el-tag-bg-color: var(--el-color-error-light-9);
		--el-tag-border-color: var(--el-color-error-light-8);
		--el-tag-hover-color: var(--el-color-error)
	}

	#app .el-tag.filled.el-tag--info {
		--el-tag-bg-color: var(--el-color-info-light-9);
		--el-tag-border-color: var(--el-color-info-light-8);
		--el-tag-hover-color: var(--el-color-info)
	}
</style>
